import { useState } from 'react';
import { useAuth } from '@/app/AuthContext.jsx';
import { useCanAsMember } from '@/hooks';
import {
  usePlatformAttentionSettings,
  useUpdatePlatformAttentionSettings,
} from '@/hooks/usePlatformAttentionSettings';
import { errorMessage } from '@/lib/recepthor-api';
import { Button, useToast } from '@/ui';
import './PlataformaAvisos.css';

const validId = (id) => /^[UW][A-Z0-9]{6,}$/.test(id);

function AttentionEditor({ settings, organizationId, canUpdate }) {
  const [ids, setIds] = useState(null);
  const [input, setInput] = useState('');
  const [addError, setAddError] = useState('');
  const [saveError, setSaveError] = useState('');
  const mutation = useUpdatePlatformAttentionSettings({ organizationId });
  const toast = useToast();
  const saved = settings?.slackUserIds ?? [];
  const current = ids ?? saved;
  const dirty = JSON.stringify(current) !== JSON.stringify(saved);
  const locked = mutation.isPending;
  const invalidList =
    current.length > 10 ||
    new Set(current).size !== current.length ||
    current.some((id) => !validId(id));

  function add(event) {
    event.preventDefault();
    if (!canUpdate || locked) return;
    setSaveError('');
    const id = input
      .trim()
      .replace(/^<@([^>]+)>$/, '$1')
      .toUpperCase();
    if (!validId(id)) {
      setAddError('Ingresa un ID de miembro válido, que comience con U o W.');
    } else if (current.includes(id)) {
      setAddError('Este ID ya está en la lista.');
    } else if (current.length >= 10) {
      setAddError('Puedes agregar un máximo de 10 personas.');
    } else {
      setIds([...current, id]);
      setInput('');
      setAddError('');
    }
  }

  function save() {
    if (!canUpdate || !dirty || locked || invalidList) return;
    setSaveError('');
    mutation.mutate(current, {
      onSuccess: () => {
        setIds(null);
        toast({ kind: 'success', message: 'Avisos guardados.' });
      },
      onError: (err) =>
        setSaveError(
          errorMessage(
            err,
            'No se pudieron guardar los avisos. Intenta nuevamente.',
          ),
        ),
    });
  }

  return (
    <section className="pla" aria-label="Personas que reciben avisos">
      <h3>Personas designadas</h3>
      <p>Esta configuración se aplica a toda la plataforma.</p>
      {current.length === 0 ? (
        <p>Nadie recibe avisos</p>
      ) : (
        <ul className="pla__list">
          {current.map((id, index) => (
            <li key={index}>
              <code>{id}</code>
              {!validId(id) ? (
                <span>ID inválido</span>
              ) : (
                index >= 10 && (
                  <span>Sobra: el máximo es 10 personas. Quita este ID.</span>
                )
              )}
              {current.indexOf(id) !== index && <span>ID repetido</span>}
              {canUpdate && (
                <Button
                  variant="secondary"
                  size="md"
                  disabled={locked}
                  onClick={() => {
                    setIds(
                      current.filter((_, itemIndex) => itemIndex !== index),
                    );
                    setAddError('');
                    setSaveError('');
                  }}
                  aria-label={`Quitar ${id}`}
                >
                  Quitar
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canUpdate ? (
        <>
          <form onSubmit={add} className="pla__add">
            <label htmlFor="slack-member-id">ID de miembro de Slack</label>
            <div>
              <input
                id="slack-member-id"
                value={input}
                onChange={(event) => {
                  setInput(event.target.value);
                  setAddError('');
                  setSaveError('');
                }}
                disabled={locked}
                aria-invalid={Boolean(addError)}
                aria-describedby={`slack-member-help${addError ? ' slack-member-error' : ''}`}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
              />
              <Button
                variant="secondary"
                size="md"
                disabled={locked}
                type="submit"
              >
                Agregar
              </Button>
            </div>
            <p id="slack-member-help">
              En Slack: perfil de la persona → ⋮ → Copiar ID de miembro
            </p>
            <p aria-live="polite">{current.length} de 10 personas</p>
            {addError && (
              <p id="slack-member-error" role="alert">
                {addError}
              </p>
            )}
          </form>
          {saveError && <p role="alert">{saveError}</p>}
          <Button
            variant="primary"
            size="md"
            onClick={save}
            disabled={!dirty || locked || invalidList}
          >
            {locked ? 'Guardando…' : 'Guardar avisos'}
          </Button>
        </>
      ) : (
        <p>Solo lectura. No tienes permiso para modificar los avisos.</p>
      )}
    </section>
  );
}

// Section of Configuración: who Slack notifies when a WhatsApp pedido needs
// attention. Reads with platform-settings:read, edits with :update.
export default function PlataformaAvisos() {
  const { organizationId } = useAuth();
  const canRead = useCanAsMember('platform-settings:read');
  const canUpdate = useCanAsMember('platform-settings:update');
  const query = usePlatformAttentionSettings({
    organizationId,
    enabled: canRead,
  });
  return (
    <section className="plc-section" aria-labelledby="plc-attention-title">
      <h2 id="plc-attention-title" className="plc-section__title">
        Avisos de Slack
      </h2>
      <p className="plc-section__body">
        Elige a quién avisa Recepthor por Slack cuando un pedido de WhatsApp
        necesita atención. Si nunca se guardó una lista, se muestra la
        configurada por defecto en el servidor.
      </p>
      {!canRead ? (
        <p>Sin acceso. No tienes permiso para leer los avisos.</p>
      ) : organizationId == null ? (
        <p>Elige una organización para ver los avisos.</p>
      ) : query.isPending ? (
        <p role="status">Cargando avisos…</p>
      ) : query.isError && query.data == null ? (
        <div role="alert">
          <p>No se pudieron cargar los avisos.</p>
          <Button onClick={() => query.refetch()}>Reintentar</Button>
        </div>
      ) : (
        <AttentionEditor
          key={organizationId}
          settings={query.data}
          organizationId={organizationId}
          canUpdate={canUpdate}
        />
      )}
    </section>
  );
}
